import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { orderService, productService, customerService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';

const OrderForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isEditing = !!id;

    // Form state
    const [formData, setFormData] = useState({
                                                 order_number: '',
                                                 customer_table_id: '', // Use customer table ID
                                                 customer_name: '',
                                                 customer_email: '',
                                                 customer_username: '',
                                                 customer_type: 'retail',
                                                 status: 'pending',
                                                 order_date: new Date(),
                                                 ship_by_date: null,
                                                 order_total: 0,
                                                 amount_paid: 0,
                                                 tax_amount: 0,
                                                 shipping_amount: 0,
                                                 customer_notes: '',
                                                 internal_notes: '',
                                                 payment_method: '',
                                                 requested_shipping_service: '',
                                                 items: [],
                                             });

    // Reference data
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [showOrderDatePicker, setShowOrderDatePicker] = useState(false);
    const [showShipByDatePicker, setShowShipByDatePicker] = useState(false);
    const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
    const [newCustomerData, setNewCustomerData] = useState({
                                                               customer_name: '',
                                                               customer_email: '',
                                                               customer_username: '',
                                                               customer_type: 'retail',
                                                               phone: '',
                                                               notes: ''
                                                           });

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                // Load customers and products
                const [customersData, productsData] = await Promise.all([
                                                                            customerService.getCustomers({ per_page: 1000, customer_type:'wholesale' }), // Get all customers
                                                                            productService.getProducts()
                                                                        ]);

                setCustomers(customersData.data?.data || customersData.data || []);
                setProducts(productsData.data?.data || productsData.data || []);

                // If editing, load the order
                if (isEditing) {
                    const orderData = await orderService.getOrder(id);
                    const order = orderData.data;
                    setFormData({
                                    ...order,
                                    customer_table_id: order.customer_table_id || '',
                                    customer_name: order.customer?.customer_name || order.customer_name || '',
                                    customer_email: order.customer?.customer_email || order.customer_email || '',
                                    customer_username: order.customer?.customer_username || order.customer_username || '',
                                    customer_type: order.customer?.customer_type || 'retail',
                                    order_date: new Date(order.order_date),
                                    ship_by_date: order.ship_by_date ? new Date(order.ship_by_date) : null,
                                    order_total: order.order_total || 0,
                                    amount_paid: order.amount_paid || 0,
                                    tax_amount: order.tax_amount || 0,
                                    shipping_amount: order.shipping_amount || 0,
                                    items: order.items || []
                                });
                }
            } catch (error) {
                console.error("Failed to load data:", error);
                toast.error("Failed to load data");
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [id, isEditing]);

    const handleAddItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [
                ...prev.items,
                { product_id: '', quantity: 1, variant: 'standard' }
            ]
        }));
    };

    const handleRemoveItem = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handleItemChange = (index, field, value) => {
        setFormData(prev => {
            const newItems = [...prev.items];
            newItems[index] = { ...newItems[index], [field]: value };
            return { ...prev, items: newItems };
        });
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleCustomerChange = (customerId) => {
        if (customerId === 'new') {
            setShowNewCustomerForm(true);
            return;
        }

        const selectedCustomer = customers.find(c => c.id == customerId);
        if (selectedCustomer) {
            setFormData(prev => ({
                ...prev,
                customer_table_id: customerId,
                customer_name: selectedCustomer.customer_name,
                customer_email: selectedCustomer.customer_email,
                customer_username: selectedCustomer.customer_username,
                customer_type: selectedCustomer.customer_type
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                customer_table_id: customerId,
                customer_name: '',
                customer_email: '',
                customer_username: '',
                customer_type: 'retail'
            }));
        }
    };

    const handleNewCustomerSubmit = async () => {
        try {
            // Validate new customer data
            if (!newCustomerData.customer_name || !newCustomerData.customer_email) {
                throw new Error('Customer name and email are required');
            }

            // Create new customer
            const response = await customerService.createCustomer(newCustomerData);
            const newCustomer = response.data;

            // Add to customers list
            setCustomers(prev => [newCustomer, ...prev]);

            // Select the new customer
            setFormData(prev => ({
                ...prev,
                customer_table_id: newCustomer.id,
                customer_name: newCustomer.customer_name,
                customer_email: newCustomer.customer_email,
                customer_username: newCustomer.customer_username,
                customer_type: newCustomer.customer_type
            }));

            // Close the form and reset
            setShowNewCustomerForm(false);
            setNewCustomerData({
                                   customer_name: '',
                                   customer_email: '',
                                   customer_username: '',
                                   customer_type: 'retail',
                                   phone: '',
                                   notes: ''
                               });

            toast.success('Customer created successfully');
        } catch (error) {
            console.error('Error creating customer:', error);
            toast.error(error.response?.data?.message || error.message || 'Failed to create customer');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);

        try {
            // Validate form
            if (!formData.customer_table_id) {
                throw new Error('Please select a customer');
            }

            if (formData.items.length === 0) {
                throw new Error('Please add at least one item to the order');
            }

            for (const item of formData.items) {
                if (!item.product_id || item.quantity <= 0) {
                    throw new Error('Please select a product and enter a valid quantity for all items');
                }
            }

            // Prepare data for submission
            const orderData = {
                ...formData,
                order_date: format(formData.order_date, 'yyyy-MM-dd HH:mm:ss'),
                ship_by_date: formData.ship_by_date ? format(formData.ship_by_date, 'yyyy-MM-dd') : null,
            };

            // Submit form
            if (isEditing) {
                await orderService.updateOrder(id, orderData);
                toast.success("Order updated successfully");
            } else {
                await orderService.createOrder(orderData);
                toast.success("Order created successfully");
            }

            // Navigate back to orders list
            navigate('/orders');
        } catch (error) {
            console.error("Error saving order:", error);
            toast.error(error.response?.data?.message || error.message || "Failed to save order");
        } finally {
            setSubmitLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {isEditing ? 'Edit Order' : 'Create New Order'}
                    </h2>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="p-6 space-y-6">
                        {/* Order Header */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div className="space-y-2">
                                <label htmlFor="orderNumber" className="block text-sm font-medium text-gray-700">
                                    Order Number
                                </label>
                                <input
                                    id="orderNumber"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                    value={formData.order_number}
                                    onChange={(e) => handleInputChange('order_number', e.target.value)}
                                    placeholder={isEditing ? '' : 'Auto-generated if left empty'}
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="customerId" className="block text-sm font-medium text-gray-700">
                                    Customer *
                                </label>
                                <div className="flex space-x-2">
                                    <select
                                        id="customerId"
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={formData.customer_table_id}
                                        onChange={(e) => handleCustomerChange(e.target.value)}
                                        required
                                    >
                                        <option value="">Select a customer</option>
                                        <option value="new">+ Add New Customer</option>

                                        {customers.map(customer => (
                                            <option key={customer.id} value={customer.id}>
                                                {customer.customer_name} ({customer.customer_email}) - {customer.customer_type}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* New Customer Form Modal */}
                                {showNewCustomerForm && (
                                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                        <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                                            <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Customer</h3>
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Name *</label>
                                                    <input
                                                        type="text"
                                                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        value={newCustomerData.customer_name}
                                                        onChange={(e) => setNewCustomerData(prev => ({...prev, customer_name: e.target.value}))}
                                                        required
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Email *</label>
                                                    <input
                                                        type="email"
                                                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        value={newCustomerData.customer_email}
                                                        onChange={(e) => setNewCustomerData(prev => ({...prev, customer_email: e.target.value}))}
                                                        required
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Username</label>
                                                    <input
                                                        type="text"
                                                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        value={newCustomerData.customer_username}
                                                        onChange={(e) => setNewCustomerData(prev => ({...prev, customer_username: e.target.value}))}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Customer Type</label>
                                                    <select
                                                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        value={newCustomerData.customer_type}
                                                        onChange={(e) => setNewCustomerData(prev => ({...prev, customer_type: e.target.value}))}
                                                    >
                                                        <option value="retail">Retail</option>
                                                        <option value="wholesale">Wholesale</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                                                    <input
                                                        type="tel"
                                                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        value={newCustomerData.phone}
                                                        onChange={(e) => setNewCustomerData(prev => ({...prev, phone: e.target.value}))}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex justify-end space-x-3 mt-6">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowNewCustomerForm(false)}
                                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleNewCustomerSubmit}
                                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                                                >
                                                    Add Customer
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                                    Status
                                </label>
                                <select
                                    id="status"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    value={formData.status}
                                    onChange={(e) => handleInputChange('status', e.target.value)}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="processing">Processing</option>
                                    <option value="fulfilled">Fulfilled</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Order Date
                                </label>
                                <div className="relative">
                                    <button
                                        type="button"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        onClick={() => setShowOrderDatePicker(!showOrderDatePicker)}
                                    >
                                        <span className="flex items-center">
                                            <svg className="h-5 w-5 text-gray-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                            </svg>
                                            {format(formData.order_date, 'MMMM d, yyyy')}
                                        </span>
                                    </button>
                                    {showOrderDatePicker && (
                                        <div className="absolute z-10 mt-1 bg-white rounded-md shadow-lg p-4 border border-gray-200">
                                            <div className="text-center mb-2">
                                                <input
                                                    type="date"
                                                    className="px-2 py-1 border border-gray-300 rounded"
                                                    value={format(formData.order_date, 'yyyy-MM-dd')}
                                                    onChange={(e) => handleInputChange('order_date', new Date(e.target.value))}
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className="w-full px-2 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                                                onClick={() => setShowOrderDatePicker(false)}
                                            >
                                                Done
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Ship By Date
                                </label>
                                <div className="relative">
                                    <button
                                        type="button"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        onClick={() => setShowShipByDatePicker(!showShipByDatePicker)}
                                    >
                                        <span className="flex items-center">
                                            <svg className="h-5 w-5 text-gray-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                            </svg>
                                            {formData.ship_by_date ? format(formData.ship_by_date, 'MMMM d, yyyy') : 'Select date'}
                                        </span>
                                    </button>
                                    {showShipByDatePicker && (
                                        <div className="absolute z-10 mt-1 bg-white rounded-md shadow-lg p-4 border border-gray-200">
                                            <div className="text-center mb-2">
                                                <input
                                                    type="date"
                                                    className="px-2 py-1 border border-gray-300 rounded"
                                                    value={formData.ship_by_date ? format(formData.ship_by_date, 'yyyy-MM-dd') : ''}
                                                    onChange={(e) => handleInputChange('ship_by_date', e.target.value ? new Date(e.target.value) : null)}
                                                />
                                            </div>
                                            <div className="flex space-x-2">
                                                <button
                                                    type="button"
                                                    className="flex-1 px-2 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
                                                    onClick={() => {
                                                        handleInputChange('ship_by_date', null);
                                                        setShowShipByDatePicker(false);
                                                    }}
                                                >
                                                    Clear
                                                </button>
                                                <button
                                                    type="button"
                                                    className="flex-1 px-2 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                                                    onClick={() => setShowShipByDatePicker(false)}
                                                >
                                                    Done
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700">
                                    Payment Method
                                </label>
                                <input
                                    id="paymentMethod"
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    value={formData.payment_method}
                                    onChange={(e) => handleInputChange('payment_method', e.target.value)}
                                    placeholder="e.g., Credit Card, PayPal"
                                />
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="shippingService" className="block text-sm font-medium text-gray-700">
                                    Requested Shipping Service
                                </label>
                                <input
                                    id="shippingService"
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    value={formData.requested_shipping_service}
                                    onChange={(e) => handleInputChange('requested_shipping_service', e.target.value)}
                                    placeholder="e.g., FedEx Ground, UPS 2-Day"
                                />
                            </div>
                        </div>

                        {/* Customer Notes */}
                        <div className="space-y-2">
                            <label htmlFor="customerNotes" className="block text-sm font-medium text-gray-700">
                                Customer Notes
                            </label>
                            <textarea
                                id="customerNotes"
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={formData.customer_notes}
                                onChange={(e) => handleInputChange('customer_notes', e.target.value)}
                                placeholder="Customer's special requests or notes..."
                            />
                        </div>

                        {/* Internal Notes */}
                        <div className="space-y-2">
                            <label htmlFor="internalNotes" className="block text-sm font-medium text-gray-700">
                                Internal Notes
                            </label>
                            <textarea
                                id="internalNotes"
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={formData.internal_notes}
                                onChange={(e) => handleInputChange('internal_notes', e.target.value)}
                                placeholder="Internal notes for staff..."
                            />
                        </div>

                        {/* Financial Information */}
                        <div>
                            <h3 className="text-lg font-medium text-gray-800 mb-4">Financial Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div className="space-y-2">
                                    <label htmlFor="orderTotal" className="block text-sm font-medium text-gray-700">
                                        Order Total ($)
                                    </label>
                                    <input
                                        id="orderTotal"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={formData.order_total}
                                        onChange={(e) => handleInputChange('order_total', parseFloat(e.target.value) || 0)}
                                        placeholder="0.00"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="amountPaid" className="block text-sm font-medium text-gray-700">
                                        Amount Paid ($)
                                    </label>
                                    <input
                                        id="amountPaid"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={formData.amount_paid}
                                        onChange={(e) => handleInputChange('amount_paid', parseFloat(e.target.value) || 0)}
                                        placeholder="0.00"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="taxAmount" className="block text-sm font-medium text-gray-700">
                                        Tax Amount ($)
                                    </label>
                                    <input
                                        id="taxAmount"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={formData.tax_amount}
                                        onChange={(e) => handleInputChange('tax_amount', parseFloat(e.target.value) || 0)}
                                        placeholder="0.00"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="shippingAmount" className="block text-sm font-medium text-gray-700">
                                        Shipping Amount ($)
                                    </label>
                                    <input
                                        id="shippingAmount"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={formData.shipping_amount}
                                        onChange={(e) => handleInputChange('shipping_amount', parseFloat(e.target.value) || 0)}
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>

                            {/* Financial Summary */}
                            <div className="mt-4 p-4 bg-gray-50 rounded-md">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Subtotal:</span>
                                        <span className="font-medium">
                                            ${((formData.order_total || 0) - (formData.tax_amount || 0) - (formData.shipping_amount || 0)).toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Balance Due:</span>
                                        <span className={`font-medium ${
                                            (formData.order_total || 0) - (formData.amount_paid || 0) > 0
                                            ? 'text-red-600'
                                            : 'text-green-600'
                                        }`}>
                                            ${((formData.order_total || 0) - (formData.amount_paid || 0)).toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Order Items */}
                        <div>
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-medium text-gray-800">Order Items</h3>
                                <button
                                    type="button"
                                    onClick={handleAddItem}
                                    className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
                                >
                                    <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                    </svg>
                                    Add Item
                                </button>
                            </div>

                            {formData.items.length === 0 ? (
                                <div className="text-center p-6 border border-dashed border-gray-300 rounded-md text-gray-500">
                                    No items added yet. Click "Add Item" to begin.
                                </div>
                            ) : (
                                 <div className="space-y-4">
                                     {formData.items.map((item, index) => (
                                         <div key={index} className="flex flex-wrap items-end gap-4 p-4 border rounded-md border-gray-300">
                                             <div className="flex-1 min-w-48 space-y-2">
                                                 <label className="block text-sm font-medium text-gray-700">Product</label>
                                                 <select
                                                     className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                     value={item.product_id}
                                                     onChange={(e) => handleItemChange(index, 'product_id', e.target.value)}
                                                     required
                                                 >
                                                     <option value="">Select a product</option>
                                                     {products.map(product => (
                                                         <option key={product.id} value={product.id}>
                                                             {product.sku} - {product.name}
                                                         </option>
                                                     ))}
                                                 </select>
                                             </div>
                                             <div className="w-24 space-y-2">
                                                 <label className="block text-sm font-medium text-gray-700">Quantity</label>
                                                 <input
                                                     type="number"
                                                     min="1"
                                                     className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                     value={item.quantity}
                                                     onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                                                     required
                                                 />
                                             </div>
                                             <div className="w-24 space-y-2">
                                                 <label className="block text-sm font-medium text-gray-700">Unit Price</label>
                                                 <input
                                                     type="number"
                                                     min="1"
                                                     className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                     value={item.unit_price}
                                                     onChange={(e) => handleItemChange(index, 'unit_price', parseInt(e.target.value) || 1)}
                                                     required
                                                 />
                                             </div>
                                             <div className="w-32 space-y-2">
                                                 <label className="block text-sm font-medium text-gray-700">Variant</label>
                                                 <select
                                                     className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                     value={item.variant}
                                                     onChange={(e) => handleItemChange(index, 'variant', e.target.value)}
                                                 >
                                                     <option value="standard">Standard</option>
                                                     <option value="carbon">Carbon</option>
                                                 </select>
                                             </div>
                                             <button
                                                 type="button"
                                                 className="p-2 text-gray-500 hover:text-red-500 focus:outline-none"
                                                 onClick={() => handleRemoveItem(index)}
                                             >
                                                 <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                     <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                 </svg>
                                             </button>
                                         </div>
                                     ))}
                                 </div>
                             )}
                        </div>
                    </div>

                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
                        <button
                            type="button"
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            onClick={() => navigate('/orders')}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${submitLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                            disabled={submitLoading}
                        >
                            {submitLoading ? 'Saving...' : isEditing ? 'Update Order' : 'Create Order'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default OrderForm;
